package org.catharsis.spendtel.feature.legal

object SpendTelTermsOfService {
    fun document(): LegalDocument = LegalDocument(
        type = LegalDocumentType.TermsAndConditions,
        title = "Terms and Conditions",
        subtitle = "Rules for using ${LegalConstants.PRODUCT_NAME}, operated by ${LegalConstants.COMPANY_NAME}.",
        lastUpdated = LegalConstants.EFFECTIVE_DATE,
        sections = listOf(
            LegalSection(
                title = "1. Agreement to Terms",
                paragraphs = listOf(
                    "These Terms and Conditions (\"Terms\") are a binding legal agreement between you and ${LegalConstants.COMPANY_NAME} (\"${LegalConstants.COMPANY_NAME}\", \"we\", \"us\", or \"our\") governing your access to and use of ${LegalConstants.PRODUCT_NAME} (the \"Service\"), including our website at ${LegalConstants.WEBSITE}, web application, and mobile applications.",
                    "By creating an account, accessing, or using the Service, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Service.",
                ),
            ),
            LegalSection(
                title = "2. Eligibility",
                paragraphs = listOf(
                    "You must be at least 18 years old and able to form a binding contract to use the Service. You represent that all registration information is accurate and that you will keep it current.",
                    "You may not use the Service if you have been suspended or removed, or if use is prohibited by applicable law.",
                ),
            ),
            LegalSection(
                title = "3. Description of the Service",
                paragraphs = listOf(
                    "${LegalConstants.PRODUCT_NAME} helps users upload financial statements and generates personalized spending reports, summaries, and insights.",
                    "The Service is provided for personal, non-commercial informational purposes only. ${LegalConstants.COMPANY_NAME} is not a bank, credit union, lender, broker-dealer, investment adviser, tax preparer, or accounting firm.",
                ),
            ),
            LegalSection(
                title = "4. Not Financial, Legal, or Tax Advice",
                paragraphs = listOf(
                    "All content, reports, insights, categorizations, personality labels, equivalences, trends, and recommendations provided through the Service are for general informational and entertainment purposes only.",
                    "They do not constitute financial, investment, legal, tax, or accounting advice. You alone are responsible for your financial decisions. Consult qualified professionals before acting on any information from the Service.",
                    "We do not guarantee accuracy, completeness, or suitability of any output. Statement parsing, categorization, and narrative generation may contain errors.",
                ),
            ),
            LegalSection(
                title = "5. Accounts and Security",
                paragraphs = listOf(
                    "You are responsible for safeguarding your credentials and for all activity under your account. Notify us immediately at ${LegalConstants.LEGAL_EMAIL} of unauthorized access.",
                    "We may suspend or terminate accounts that violate these Terms or pose security, legal, or operational risk.",
                ),
            ),
            LegalSection(
                title = "6. User Content and Uploaded Statements",
                paragraphs = listOf(
                    "You retain ownership of statements and other content you upload (\"User Content\"). You grant ${LegalConstants.COMPANY_NAME} a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, analyze, and display User Content solely to operate, provide, and improve the Service.",
                    "You represent that you have all rights necessary to upload User Content and that doing so does not violate any law or third-party rights.",
                    "You must not upload malware, unlawful content, content you do not have rights to use, or information belonging to others without authorization.",
                ),
            ),
            LegalSection(
                title = "7. Acceptable Use",
                paragraphs = listOf(
                    "You agree not to:",
                ),
                bullets = listOf(
                    "Use the Service for any unlawful purpose or in violation of these Terms.",
                    "Reverse engineer, decompile, scrape, or attempt to extract source code or models except as permitted by law.",
                    "Interfere with or disrupt the Service, servers, or networks.",
                    "Access accounts or data without authorization.",
                    "Use the Service to provide competing services or resell access without our written consent.",
                    "Misrepresent outputs as professional financial advice or guaranteed results.",
                ),
            ),
            LegalSection(
                title = "8. Payments, Credits, and Refunds",
                paragraphs = listOf(
                    "Certain features require report credits or payment. Prices and billing terms are shown at purchase.",
                    "Except where required by law, all fees are non-refundable once a report credit is consumed or a report generation has started. We may change pricing with notice for future purchases.",
                    "Payment processing is handled by third-party providers. Your payment is also subject to their terms.",
                ),
            ),
            LegalSection(
                title = "9. Intellectual Property",
                paragraphs = listOf(
                    "The Service, including software, design, branding, templates, and generated report formats (excluding your underlying statement data), is owned by ${LegalConstants.COMPANY_NAME} or its licensors and protected by intellectual property laws.",
                    "We grant you a limited, personal, non-transferable, non-exclusive, revocable license to use the Service in accordance with these Terms.",
                ),
            ),
            LegalSection(
                title = "10. Disclaimer of Warranties",
                paragraphs = listOf(
                    "THE SERVICE AND ALL CONTENT ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY.",
                    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${LegalConstants.COMPANY_NAME} DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AND QUIET ENJOYMENT.",
                    "WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS, OR THAT RESULTS WILL BE ACCURATE OR RELIABLE.",
                ),
            ),
            LegalSection(
                title = "11. Limitation of Liability",
                paragraphs = listOf(
                    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${LegalConstants.COMPANY_NAME} AND ITS OFFICERS, DIRECTORS, MEMBERS, EMPLOYEES, AGENTS, AFFILIATES, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING FROM OR RELATED TO THE SERVICE OR THESE TERMS, EVEN IF ADVISED OF THE POSSIBILITY.",
                    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO ${LegalConstants.COMPANY_NAME} FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY, OR (B) ONE HUNDRED U.S. DOLLARS (US \$100).",
                    "Some jurisdictions do not allow certain limitations; in those jurisdictions, our liability is limited to the fullest extent permitted by law.",
                ),
            ),
            LegalSection(
                title = "12. Indemnification",
                paragraphs = listOf(
                    "You agree to defend, indemnify, and hold harmless ${LegalConstants.COMPANY_NAME} and its officers, directors, members, employees, agents, and affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys’ fees) arising from: (a) your use of the Service; (b) your User Content; (c) your violation of these Terms; or (d) your violation of any law or third-party rights.",
                ),
            ),
            LegalSection(
                title = "13. Dispute Resolution and Arbitration",
                paragraphs = listOf(
                    "Please contact us first at ${LegalConstants.LEGAL_EMAIL} so we can try to resolve disputes informally.",
                    "Except where prohibited by law, any dispute arising out of or relating to these Terms or the Service will be resolved by binding individual arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, rather than in court. You and ${LegalConstants.COMPANY_NAME} waive any right to a jury trial or to participate in a class action, class arbitration, or representative proceeding.",
                    "Either party may bring qualifying claims in small claims court. Either party may seek injunctive relief in court for misuse of intellectual property or unauthorized access.",
                    "This section survives termination.",
                ),
            ),
            LegalSection(
                title = "14. Governing Law",
                paragraphs = listOf(
                    "These Terms are governed by the laws of the State of ${LegalConstants.GOVERNING_LAW_STATE}, without regard to conflict-of-law principles, except that the Federal Arbitration Act governs arbitration.",
                ),
            ),
            LegalSection(
                title = "15. Termination",
                paragraphs = listOf(
                    "You may stop using the Service at any time and may delete your account in Settings.",
                    "We may suspend or terminate your access at any time, with or without notice, for any reason, including violation of these Terms. Upon termination, your license to use the Service ends. Sections intended to survive (including disclaimers, limitations of liability, indemnification, and dispute resolution) survive termination.",
                ),
            ),
            LegalSection(
                title = "16. Changes to the Service or Terms",
                paragraphs = listOf(
                    "We may modify the Service or these Terms at any time. We will post updated Terms and update the \"Last updated\" date. Material changes may be notified by email or in-app notice where required. Continued use after changes become effective constitutes acceptance.",
                ),
            ),
            LegalSection(
                title = "17. General",
                paragraphs = listOf(
                    "These Terms, together with the Privacy Policy, are the entire agreement between you and ${LegalConstants.COMPANY_NAME} regarding the Service.",
                    "If any provision is unenforceable, the remaining provisions remain in effect. Our failure to enforce a provision is not a waiver.",
                    "You may not assign these Terms without our consent. We may assign these Terms without restriction.",
                ),
            ),
            LegalSection(
                title = "18. Contact",
                paragraphs = listOf(
                    "${LegalConstants.COMPANY_NAME}\nEmail: ${LegalConstants.LEGAL_EMAIL}\nWebsite: ${LegalConstants.WEBSITE}",
                ),
            ),
        ),
    )
}
